export function clean(uri: string): string {
	return uri.replace(/\/+/g, '/').replace(/\/+$/, '');
}

const REGEX_LOOKUP_PATTERN = /(:[^/]+)(?:\\\?)?/g;
const REGEX_REPLACE_PATTERN = /:([^/]+)/;
const REGEX_LOOKUP_CAPTURE = '([^/]+)';
const REGEX_OPTIONAL_LOOKUP_CAPTURE = '(?:([^/]+))?';
const REGEX_CATCH_ALL_CAPTURE = '(.+)';
const REGEX_LOOKUP_QUERY = /\S[^?]*(?:\?+|$)/g;

/** Strips the `?`/`*` modifier off a `:param` name, e.g. `pagePath*` -> `pagePath`. */
function parameterName(match: string): string {
	return match.replace(':', '').replace('?', '').replace('*', '');
}

export function toRegExp(path: string): RegExp {
	let pattern: string = clean(`/${path}`)
		.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
		.replace(REGEX_LOOKUP_PATTERN, (match: string) => {
			// @NOTE Modifiers are escaped by the pass above, so `*` arrives as `\*`
			if (match.endsWith('*') || match.endsWith('*\\')) {
				return REGEX_CATCH_ALL_CAPTURE;
			}

			if (match.endsWith('?') || match.endsWith('?\\')) {
				return REGEX_OPTIONAL_LOOKUP_CAPTURE;
			}

			return REGEX_LOOKUP_CAPTURE;
		});

	// Nested inherited cannot be optional, make sure capture group is required
	if (pattern.match(/\)\?\//g)) {
		pattern = pattern.replace('(?:[^/]+)?/', '([^/]+)/');
	}

	// Adjust optional regex to include trailing slash
	if (pattern.match(/\/\(\?/g)) {
		pattern = pattern.replace('\\/(?:', '\\/?(?:');
	}

	// A catch-all swallows its own leading slash so the segment is genuinely
	// optional: `docs/:pagePath*` has to match a bare `/docs` (the section
	// index) as well as `/docs/foo/bar/baz`. Applied last so the adjustments
	// above never see the group it emits.
	pattern = pattern.replace(/\\\/\(\.\+\)/g, '(?:\\/(.+))?');

	if (pattern.length === 0) {
		pattern = '\\/';
	}

	return new RegExp(`^${pattern}$`, 'i');
}

interface RouterPathParams {
	[key: string]: boolean | string | number;
}

export interface RouterPath {
	match: boolean;
	path: string;
	pattern: string;
	params: RouterPathParams;
	query: RouterPathParams;
}

export const EmptyRouterPath: RouterPath = {
	match: true,
	path: '',
	pattern: '',
	params: {},
	query: {},
};

function unserializeValue(val: string): boolean | string | number {
	val = decodeURIComponent(val);

	if (val === 'true') return true;

	if (val === 'false') return false;

	if (!Number.isNaN(Number(val))) {
		return Number(val);
	}

	return val;
}

function unserialize(unresolvedString: string): RouterPathParams {
	const unserializedObject: RouterPathParams = {};
	const urlSearchParams: URLSearchParams = new URLSearchParams(
		unresolvedString
	);

	for (const [key, value] of urlSearchParams.entries()) {
		const decodedKey: string = decodeURIComponent(key);

		const decodedValue: boolean | string | number = unserializeValue(
			decodeURIComponent(value)
		);

		unserializedObject[decodedKey] = decodedValue;
	}

	return unserializedObject;
}

function associate(
	keys: string[],
	values: (boolean | string | number)[]
): RouterPathParams {
	const targetObject: RouterPathParams = {};

	if (keys.length !== values.length) {
		throw new RangeError('Keys and value arrays must match in size.');
	}

	return keys.reduce((target, key, index) => {
		if (values[index]) {
			target[key] = values[index];
		}

		return target;
	}, targetObject);
}

export function parse(pattern: string, uri: string): RouterPath {
	uri = clean(uri);

	if (uri.length === 0) {
		uri = '/';
	}

	if (!pattern.startsWith('/')) {
		pattern = `/${pattern}`;
	}

	if (!uri.startsWith('/')) {
		uri = `/${uri}`;
	}

	if (uri.endsWith('/')) {
		uri = uri.substring(-1);
	}

	const uriPatternRegex: RegExp = toRegExp(pattern);
	const parameterMatches = pattern.match(REGEX_LOOKUP_PATTERN) as string[];
	const uriMatches = uri.match(REGEX_LOOKUP_QUERY) as string[];

	const routerPath: RouterPath = {
		match: false,
		path: uri.split(/[?#]/)[0] ?? '',
		pattern,
		params: {},
		query: {},
	};

	if (uriMatches !== null && uriMatches !== undefined) {
		if (uriMatches.length <= 1) {
			uriMatches.push('', '');
		}

		const [parsedUriPath, parsedUriQuery] = uriMatches.map((match) =>
			match.replace('?', '')
		);

		const parsedUriMatches = parsedUriPath?.match(uriPatternRegex) as string[];

		// @NOTE Unserialize query string if it exists
		if (typeof parsedUriQuery === 'string' && parsedUriQuery.length > 0) {
			const uriQueryObject: RouterPathParams = unserialize(parsedUriQuery);

			routerPath.query = uriQueryObject;
		}

		// @NOTE Path matches pattern
		if (parsedUriMatches !== null && parsedUriMatches !== undefined) {
			routerPath.match = true;

			// @NOTE Filter out any non strings and remove full match from result
			const filteredMatches: string[] = parsedUriMatches
				.filter((match) => typeof match === 'string')
				.slice(1);

			const hasFilteredMatches: boolean =
				filteredMatches !== null && filteredMatches !== undefined;

			const hasParameterMatches: boolean =
				parameterMatches !== null && parameterMatches !== undefined;

			if (hasFilteredMatches === true && hasParameterMatches === true) {
				const keys: string[] = parameterMatches.map(parameterName);

				const values: (boolean | string | number)[] =
					filteredMatches.map(unserializeValue);

				if (keys.length === values.length) {
					const matchParams = associate(keys, values);

					routerPath.params = matchParams;
				}
			}
		}
	}

	return routerPath;
}

export function transform(
	pattern: string,
	params: RouterPathParams = {}
): string {
	const omitKeys: string[] = [];

	const replaceMatch = (match: string, matchedKey: string): string => {
		// `:pagePath*` / `:id?` carry their modifier into the capture; the params
		// object is keyed by the bare name. Bound to a new const rather than
		// reassigned, so narrowing on `params[key]` survives.
		const key = matchedKey.replace(/[*?]$/, '');

		if (!!params[key] === true) {
			omitKeys.push(key);

			if (params[key] === null) {
				return '';
			}

			return params[key].toString();
		}

		return '';
	};

	let queryString: string;
	let queryParams: RouterPathParams = {};

	const queryPosition: number = pattern.indexOf('?');
	const hasQueryInPattern: boolean = queryPosition > -1;

	if (hasQueryInPattern) {
		queryString = pattern.substring(queryPosition + 1);
		pattern = pattern.substring(0, queryPosition);

		queryParams = unserialize(queryString);

		// @NOTE Allow parameter values in query string
		Object.entries(queryParams).forEach(([key, value]) => {
			if (typeof value === 'string') {
				if (value.substring(0, 1) === ':') {
					const param: string = (params[value.substring(1)] as string) || '';

					if (param) {
						omitKeys.push(key);

						queryParams[key] = param;
					}
				}
			}
		});
	}

	let transformedUrl: string = clean(pattern).replace(
		REGEX_REPLACE_PATTERN,
		replaceMatch
	);

	// @NOTE Append params without any replace wildcards
	Object.entries(params).forEach(([key, value]) => {
		if (!omitKeys.includes(key)) {
			queryParams[key] = value;
		}
	});

	if (!transformedUrl.startsWith('/')) {
		transformedUrl = `/${transformedUrl}`;
	}

	// @NOTE An absent catch-all/optional param leaves a dangling slash behind
	// (`docs/:pagePath*` with no params -> `/docs/`), so trim it. Guarded so a
	// bare `/` survives.
	if (transformedUrl.length > 1 && transformedUrl.endsWith('/')) {
		transformedUrl = transformedUrl.slice(0, -1);
	}

	return transformedUrl;
}

export const isLocalPathOrUrl = (pathOrUrl: string): boolean => {
	if (!pathOrUrl) return true;

	if (pathOrUrl.startsWith('webcal:')) return false;

	if (pathOrUrl.startsWith('/') && !pathOrUrl.startsWith('//')) {
		return true;
	} else if (pathOrUrl.startsWith('#')) {
		return true;
	} else if (pathOrUrl.startsWith('javascript')) {
		return true;
	}

	try {
		const url = new URL(pathOrUrl);

		if (window.location.hostname === url.hostname) {
			return true;
		}

		if (
			!url.hostname ||
			url.hostname === 'localhost' ||
			url.hostname.endsWith('.local')
		) {
			return true;
		}

		return false;
	} catch {
		return false;
	}
};

/**
 *	Adds state as querystring to a path. If shouldStringify is true it converts state to json.
 *
 *	@param string location
 *	@param string|object state
 *
 *	@returns string
 */
export function link(
	location: string,
	state: Record<string, any> = {},
	shouldSerialize: boolean = true
) {
	if (typeof state === 'object') {
		if (Object.keys(state)?.length > 0) {
			if (shouldSerialize) {
				const params = new URLSearchParams();

				Object.entries(state).forEach(([key, value]) => {
					if (typeof value !== 'string') {
						value = JSON.stringify(value);
					}

					params.append(key, value.toString());
				});

				location += `?${params}`;
			} else {
				const qs = new URLSearchParams(state);
				location += `?${qs}`;
			}
		}
	} else if (typeof state === 'string' && !(state as string).startsWith('?')) {
		location += `?${state}`;
	} else {
		location += state;
	}

	return location.replace(/\?{2,}/g, '?');
}
